/** Undo/redo snapshots for in-game Settings (localStorage keys only). */

import { KEYBIND_ACTIONS, resetKeybindKey } from "./keybind-settings";

export const SETTINGS_HISTORY_KEYS = [
    "settingBallsColor",
    "settingCursorColor",
    "settingButtonsColor",
    "settingButtonsAutoText",
    "settingTextColor",
    "settingTrailColor",
    "settingMenuBackgroundColor",
    "settingGameBackgroundColor",
    "settingMenuBackgroundBright",
    "settingGameBackgroundBright",
    "settingWASDSensitivity",
    "settingMusicVolume",
    "settingGameMusicVolume",
    "settingMenuSong",
    "settingGameSong",
    "settingBounceVolume",
    "settingPickupVolume",
    "settingGameSfxVolume",
    "settingDarknessVolume",
    "settingUiVolume",
    "settingEffectFlashes",
    "settingEffectShake",
    "settingEffectParticles",
    "settingFreezeOnLose",
    "settingFreezeOnLoseSeconds",
    "settingAutoRetry",
    "settingQuickRestart",
    "settingModesSelectPanel",
    "settingPlayTransition",
    "settingTrailEnabled",
    "settingCosmeticsMatchCursor",
    "settingNeonBalls",
    "settingHomeAmbientBalls",
    "settingNewsInHome",
    "settingNotificationsInHome",
    "settingHomeSplash",
    "settingHomeShowFriends",
    "settingHomeFriendsFilter",
    "settingHomeShowLastMode",
    "settingHomeCursorBounce",
    "settingHomeShowTrending",
    "settingHomeShowDots",
    "settingHomeShowStreak",
    "settingHomeShowChallenge",
    "settingHomeShowLootDrops",
    "settingHomeShowNewInShop",
    "settingHomeShowSuggested",
    "settingHomeShowContinue",
    "settingHomeLayoutV1",
    "settingShowFps",
    "settingShowFpsWhere",
    "settingFpsLimit",
    "settingInvertReturnPause",
    "settingInvertSidebar",
    "settingHoldReturnHome",
    "settingHoldReturnHomeSeconds",
    "settingSounds",
    "settingTouchCursorJoystick",
    "settingTouchCursorJoystickVisible",
    "settingTouchJoystickSensitivity",
    "settingShowKeybindIndicator",
    "settingShowMobileSettingsInUi",
    "keybindReturn",
    "keybindPause",
    "keybindMenu",
    "keybindPlayAgain",
    "keybindNextLevel",
    "keybindQuickRestart",
    "dpadInsetRight",
    "dpadInsetBottom",
    "dpadInsetLeft",
    "dpadLayout",
    "dpadButtonOffsets",
    "dpadLayout3Pos",
    "settingDpadEightDirections",
    "dpadButtonScale",
    "dpadButtonOpacity",
    "settingDpadGridSnap",
    "settingGamepadSensitivity",
    "settingGamepadDeadzone",
    "settingGamepadClickSource",
    "settingGamepadPauseBtn",
    "settingGamepadReturnBtn",
    "settingGamepadMenuReturnBtn",
    "settingGamepadMenuBtn",
    "settingGamepadRestartBtn",
] as const;

/** Same keys used by Settings Copy / Paste and cloud account sync. */
export const SETTINGS_SHARE_KEYS: readonly string[] = SETTINGS_HISTORY_KEYS;

export type SettingsHistorySnapshot = Record<string, string>;

export function exportSettingsHistorySnapshot(): SettingsHistorySnapshot {
    const o: SettingsHistorySnapshot = {};
    for (const k of SETTINGS_HISTORY_KEYS) {
        const v = localStorage.getItem(k);
        if (v !== null) o[k] = v;
    }
    return o;
}

/** Full snapshot for Copy — every settings key, using `fallback` when not in localStorage. */
export function exportFullSettingsShareSnapshot(
    fallback: (key: (typeof SETTINGS_HISTORY_KEYS)[number]) => string,
): SettingsHistorySnapshot {
    const o: SettingsHistorySnapshot = {};
    for (const k of SETTINGS_HISTORY_KEYS) {
        const v = localStorage.getItem(k);
        o[k] = v !== null ? v : fallback(k);
    }
    return o;
}

/** Ordered JSON for Settings Copy (includes every key). Supports legacy flat paste. */
export function serializeSettingsShareJson(values: SettingsHistorySnapshot): string {
    const settings: SettingsHistorySnapshot = {};
    for (const k of SETTINGS_HISTORY_KEYS) {
        settings[k] = typeof values[k] === "string" ? values[k] : "";
    }
    return JSON.stringify({ v: 1, settings }, null, 2);
}

/** Strip BOM / markdown fences from clipboard paste. */
export function normalizeSettingsSharePasteText(text: string): string {
    let t = (text || "").trim().replace(/^\uFEFF/, "");
    const fence = /^```(?:json)?\s*([\s\S]*?)\s*```$/i.exec(t);
    if (fence) t = fence[1].trim();
    return t;
}

/** Coerce pasted JSON values into localStorage strings. */
export function coerceSettingsShareValue(value: unknown): string | null {
    if (value == null) return null;
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (typeof value === "object") {
        try {
            return JSON.stringify(value);
        } catch {
            return null;
        }
    }
    return null;
}

/** Unwrap `{ v, settings }`, a stringified `settings` field, or return flat payload. */
export function unwrapSettingsSharePayload(parsed: unknown): Record<string, unknown> | null {
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    const o = parsed as Record<string, unknown>;
    if ("settings" in o) {
        const inner = o.settings;
        if (inner && typeof inner === "object" && !Array.isArray(inner)) {
            return inner as Record<string, unknown>;
        }
        if (typeof inner === "string") {
            try {
                const nested = JSON.parse(inner.trim()) as unknown;
                if (nested && typeof nested === "object" && !Array.isArray(nested)) {
                    return nested as Record<string, unknown>;
                }
            } catch {
                /* fall through to flat payload */
            }
        }
    }
    return o;
}

/** Parse Copy/Paste JSON (wrapped `{ v, settings }` or legacy flat object). */
export function parseSettingsShareJson(text: string): SettingsHistorySnapshot | null {
    const normalized = normalizeSettingsSharePasteText(text);
    if (!normalized) return null;
    try {
        const parsed = JSON.parse(normalized) as unknown;
        const raw = unwrapSettingsSharePayload(parsed);
        if (!raw) return null;
        const out: SettingsHistorySnapshot = {};
        for (const k of SETTINGS_HISTORY_KEYS) {
            const coerced = coerceSettingsShareValue(raw[k]);
            if (coerced != null) out[k] = coerced;
        }
        const legacyFx = coerceSettingsShareValue(raw.settingEffects);
        if (legacyFx != null) out.settingEffects = legacyFx;
        return Object.keys(out).length > 0 ? out : null;
    } catch {
        return null;
    }
}

export function settingsShareSnapshotIsComplete(snap: SettingsHistorySnapshot): boolean {
    return SETTINGS_HISTORY_KEYS.every((k) => Object.prototype.hasOwnProperty.call(snap, k));
}

/** Apply a parsed share snapshot (full replace or merge unknown keys only). */
export function applySettingsShareSnapshot(
    snap: SettingsHistorySnapshot,
    opts?: { fullReplace?: boolean },
): void {
    const fullReplace = opts?.fullReplace === true || settingsShareSnapshotIsComplete(snap);
    if (fullReplace) {
        applySettingsHistorySnapshot(snap);
        return;
    }
    for (const k of SETTINGS_HISTORY_KEYS) {
        if (!Object.prototype.hasOwnProperty.call(snap, k)) continue;
        const v = snap[k];
        if (v === "") localStorage.removeItem(k);
        else localStorage.setItem(k, v);
    }
}

export function snapshotsEqual(a: SettingsHistorySnapshot, b: SettingsHistorySnapshot): boolean {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const k of keys) {
        if ((a[k] ?? "") !== (b[k] ?? "")) return false;
    }
    return true;
}

export function applySettingsHistorySnapshot(raw: SettingsHistorySnapshot): void {
    for (const k of SETTINGS_HISTORY_KEYS) {
        if (Object.prototype.hasOwnProperty.call(raw, k)) {
            const v = raw[k];
            if (v === "" || v == null) localStorage.removeItem(k);
            else localStorage.setItem(k, String(v));
        } else {
            localStorage.removeItem(k);
        }
    }
    if (Object.prototype.hasOwnProperty.call(raw, "settingEffects")) {
        localStorage.setItem("settingEffects", raw.settingEffects!);
    } else {
        localStorage.removeItem("settingEffects");
    }
}

/** Legacy keys that can repopulate settings after a full reset if left in storage. */
const SETTINGS_LEGACY_KEYS = [
    "settingEffects",
    "settingBackgroundColor",
    "settingMenuBackgroundDarkMode",
    "settingGameBackgroundDarkMode",
    "settingMenuBackgroundBrightV100",
    "settingGameBackgroundBrightV100",
] as const;

export function clearAllSettingsStorageKeys(): void {
    for (const k of SETTINGS_HISTORY_KEYS) {
        localStorage.removeItem(k);
    }
    for (const k of SETTINGS_LEGACY_KEYS) {
        localStorage.removeItem(k);
    }
    for (const action of KEYBIND_ACTIONS) {
        resetKeybindKey(action);
    }
}
